import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Observable,
  BehaviorSubject,
  map,
  catchError,
  of,
  from,
  switchMap,
  firstValueFrom,
  take,
} from 'rxjs';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { environment } from '../../../environments/environment';
import {
  AccountBalances,
  AccountRole,
  AgentAllowance,
  CreditAccount,
  FundLock,
  FundLockStatus,
  FundLockType,
  Payment,
  UserBasicContainer,
  UserType,
} from '@enrollhere/classes';
import { parseDateContainer } from '../misc/parseDateContainer';

export interface SpendingValidation {
  valid: boolean;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/** A direct downline agency a parent can transfer funds into (AE-5597). */
export interface DownlineAgency {
  id: string;
  name?: string;
}

export interface AccountUpdateRequest {
  id: string;
  type: string;
  requestedBy?: { id: string; type: UserType };
  payload?: Record<string, unknown>;
  updates?: Record<string, unknown>;
  requestMeta?: {
    ip?: string;
    userAgent?: string;
    method?: string;
    timestamp?: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class CreditAccountService {
  private authService = inject(AuthService);
  private http = inject(HttpClient);
  private toastService = inject(ToastService);

  private baseUrl = `${environment.payments.endpoint}/credit-accounts`;

  private loadingSubject = new BehaviorSubject<boolean>(false);

  public loading$ = this.loadingSubject.asObservable();

  public accountCache: { [key: string]: CreditAccount } = {};

  private async getAuthHeaders(): Promise<HttpHeaders> {
    try {
      const token = await this.authService.getIdToken();
      if (!token) {
        throw new Error(
          'Authentication token not available. Please log in again.'
        );
      }
      return new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });
    } catch (error) {
      console.error('Error getting auth headers', error);
      throw new Error(
        'Failed to get authentication token. Please log in again.'
      );
    }
  }

  private handleError<T>(operation = 'operation', result?: T, silent = false) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      if (!silent) {
        this.toastService.error(
          'Operation failed',
          error.error?.error || error.message
        );
      }
      return of(result as T);
    };
  }

  // Load user's credit accounts and balances
  loadUserAccounts = async (
    agencyId?: string,
    agentId?: string
  ): Promise<
    ApiResponse<{
      balances: AccountBalances;
      accounts: CreditAccount[];
      userId?: string;
    }>
  > => {
    console.log('loadUserAccounts', {
      agencyId,
      agentId,
    });
    this.loadingSubject.next(true);

    // if (agentId && !agencyId) {
    //   throw new Error(
    //     'Agency ID is required when loading accounts for an agent'
    //   );
    // }

    // Build URL with optional agencyId query parameter
    let url = this.baseUrl;
    if (agencyId) {
      url += `?agencyId=${encodeURIComponent(agencyId)}`;
      if (agentId) {
        url += `&agentId=${encodeURIComponent(agentId)}`;
      }
    } else {
      if (agentId) {
        url += `?agentId=${encodeURIComponent(agentId)}`;
      }
    }

    const headers = await this.getAuthHeaders();

    try {
      const response = await firstValueFrom(
        this.http.get<
          ApiResponse<{
            balances: AccountBalances;
            accounts: CreditAccount[];
            userId?: string;
          }>
        >(url, { headers })
      );

      if (response.data) {
        response.data.accounts = response.data.accounts.map(account =>
          CreditAccount.fromJSON(account)
        );

        console.log('response.data.accounts', response.data.accounts);
      }

      this.loadingSubject.next(false);
      return response;
    } catch (error) {
      this.loadingSubject.next(false);
      console.error('loadUserAccounts failed', error);
      this.toastService.error('Operation failed', (error as Error).message);
      throw new Error();
    }
  };

  private runningRequests: {
    [key: string]: Observable<ApiResponse<CreditAccount>>;
  } = {};

  // Get specific account details. `silent` skips the error toast for callers
  // that show their own failure state (e.g. account-card in table rows).
  getAccount(
    account: CreditAccount,
    useCache = false,
    silent = false
  ): Observable<ApiResponse<CreditAccount>> {
    let url = `${this.baseUrl}/${encodeURIComponent(account.id!)}`;
    if (account.owner?.id) {
      if (account.owner.type === UserType.AGENT) {
        url += `?agentId=${encodeURIComponent(account.owner.id)}`;
      } else {
        url += `?agencyId=${encodeURIComponent(account.owner.id)}`;
      }
    }

    // The error handler is baked into the stored observable, so silent and
    // toasting callers must not share an entry.
    const requestKey = silent ? `${url}#silent` : url;

    if (this.runningRequests[requestKey]) {
      return this.runningRequests[requestKey];
    }
    console.log('getAccount url', url);

    const accountUniqueId = `${account.owner?.type}-${account.owner?.id}-${account.id}`;
    if (this.accountCache[accountUniqueId] && useCache) {
      console.log('getAccount cache hit', accountUniqueId);
      return of({
        success: true,
        data: this.accountCache[accountUniqueId],
      });
    }

    this.runningRequests[requestKey] = from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        from(this.authService.currentUser$).pipe(
          take(1),
          map(currentUser => {
            console.log('getAccount currentUser', currentUser);
            // If current user is agency and account owner is agent, append &agencyId
            let finalUrl = url;
            if (
              currentUser?.userType === UserType.AGENCY &&
              account.owner?.type === UserType.AGENT &&
              currentUser.id
            ) {
              if (!finalUrl.includes('agencyId=')) {
                // If url already has a query param, use &; otherwise, use ?
                finalUrl +=
                  (finalUrl.includes('?') ? '&' : '?') +
                  `agencyId=${encodeURIComponent(currentUser.id)}`;
              }
            }
            return { headers, finalUrl };
          }),
          switchMap(({ headers, finalUrl }) =>
            this.http
              .get<ApiResponse<CreditAccount>>(finalUrl, { headers })
              .pipe(
                map(response => {
                  delete this.runningRequests[requestKey];
                  console.log('getAccount response', response);
                  const account = response.data
                    ? CreditAccount.fromJSON(response.data)
                    : undefined;
                  if (account) {
                    this.accountCache[accountUniqueId] = account;
                  }
                  return {
                    ...response,
                    data: account,
                  };
                })
              )
          )
        )
      ),
      catchError(
        this.handleError<ApiResponse<CreditAccount>>(
          'getAccount',
          undefined,
          silent
        )
      )
    );

    return this.runningRequests[requestKey];
  }

  // Get account balance
  getAccountBalance(
    account: CreditAccount
  ): Observable<ApiResponse<AccountBalances>> {
    console.log('getAccountBalance', account);
    let url = `${this.baseUrl}/${encodeURIComponent(account.id!)}/balance`;
    if (account.owner?.id) {
      if (account.owner.type === UserType.AGENT) {
        url += `?agentId=${encodeURIComponent(account.owner.id)}`;
      } else {
        url += `?agencyId=${encodeURIComponent(account.owner.id)}`;
      }
    }

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        from(this.authService.currentUser$).pipe(
          take(1),
          map(currentUser => {
            let finalUrl = url;
            if (
              currentUser?.userType === UserType.AGENCY &&
              account.owner?.type === UserType.AGENT &&
              currentUser.id
            ) {
              if (!finalUrl.includes('agencyId=')) {
                finalUrl +=
                  (finalUrl.includes('?') ? '&' : '?') +
                  `agencyId=${encodeURIComponent(currentUser.id)}`;
              }
            }
            return { headers, finalUrl };
          }),
          switchMap(({ headers, finalUrl }) =>
            this.http
              .get<ApiResponse<AccountBalances>>(finalUrl, { headers })
              .pipe(
                map(response => ({
                  ...response,
                  data: response.data
                    ? AccountBalances.fromJSON(response.data)
                    : undefined,
                }))
              )
          )
        )
      ),
      catchError(
        this.handleError<ApiResponse<AccountBalances>>('getAccountBalance')
      )
    );
  }

  // Create new credit account
  createAccount(
    accountData: CreditAccount
  ): Observable<ApiResponse<CreditAccount>> {
    console.log('createAccount accountData', accountData);
    const payload = {
      ...accountData,
    };

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<CreditAccount>>(this.baseUrl, payload, {
          headers,
        })
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Account created successfully');
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<CreditAccount>>('createAccount'))
    );
  }

  // Update account
  updateAccount(
    account: CreditAccount,
    updates: Partial<CreditAccount>
  ): Observable<ApiResponse<CreditAccount>> {
    const url = `${this.baseUrl}/${encodeURIComponent(account.id!)}`;

    updates.owner = account.owner;

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.put<ApiResponse<CreditAccount>>(url, updates, { headers })
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Account updated successfully');
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<CreditAccount>>('updateAccount'))
    );
  }

  // Delete account
  deleteAccount(account: CreditAccount): Observable<ApiResponse<void>> {
    let url = `${this.baseUrl}/${encodeURIComponent(account.id!)}`;
    if (account.owner?.id) {
      url += `?agencyId=${encodeURIComponent(account.owner.id)}`;
    }

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.delete<ApiResponse<void>>(url, { headers })
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Account deleted successfully');
          this.loadUserAccounts(account.owner?.id);
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<void>>('deleteAccount'))
    );
  }

  // Grant role to user
  grantRole(
    account: CreditAccount,
    targetUserId: string,
    role: AccountRole,
    expiresAt?: Date
  ): Observable<ApiResponse<void>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    const payload = {
      owner: account.owner,
      targetUserId,
      role,
      expiresAt: expiresAt?.toISOString(),
    };

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<void>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/grant-role`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Role granted successfully');
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<void>>('grantRole'))
    );
  }

  // Revoke role from user
  revokeRole(
    account: CreditAccount,
    targetUserId: string,
    role: AccountRole
  ): Observable<ApiResponse<void>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    const payload = { owner: account.owner, targetUserId, role };

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<void>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/revoke-role`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Role revoked successfully');
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<void>>('revokeRole'))
    );
  }

  // Create agent allowance
  createAllowance(
    account: CreditAccount,
    allowanceData: Partial<AgentAllowance>
  ): Observable<ApiResponse<AgentAllowance>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    const payload = AgentAllowance.toJSON(allowanceData) as {
      [key: string]: unknown;
    };

    payload['owner'] = UserBasicContainer.toJSON(account.owner!);

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<AgentAllowance>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/create-allowance`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Allowance created successfully');
        }
        return response;
      }),
      catchError(
        this.handleError<ApiResponse<AgentAllowance>>('createAllowance')
      )
    );
  }

  getAccountUpdateRequests(
    account: CreditAccount,
    opts?: { limit?: number; startAfter?: string }
  ): Observable<
    ApiResponse<{
      items: AccountUpdateRequest[];
      nextCursor?: string;
    }>
  > {
    if (!account.id) {
      throw new Error('Account ID is required');
    }

    const params = new URLSearchParams();
    if (account.owner?.id) {
      if (account.owner.type === UserType.AGENT) {
        params.set('agentId', account.owner.id);
      } else {
        params.set('agencyId', account.owner.id);
      }
    }
    if (opts?.limit != null) {
      params.set('limit', String(opts.limit));
    }
    if (opts?.startAfter) {
      params.set('startAfter', opts.startAfter);
    }
    const qs = params.toString();
    const path = `${this.baseUrl}/${encodeURIComponent(account.id)}/update-requests`;
    const baseUrl = `${path}${qs ? `?${qs}` : ''}`;

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        from(this.authService.currentUser$).pipe(
          take(1),
          map(currentUser => {
            let finalUrl = baseUrl;
            if (
              currentUser?.userType === UserType.AGENCY &&
              account.owner?.type === UserType.AGENT &&
              currentUser.id
            ) {
              if (!finalUrl.includes('agencyId=')) {
                finalUrl +=
                  (finalUrl.includes('?') ? '&' : '?') +
                  `agencyId=${encodeURIComponent(currentUser.id)}`;
              }
            }
            return { headers, finalUrl };
          }),
          switchMap(({ headers, finalUrl }) =>
            this.http.get<
              ApiResponse<{
                items: AccountUpdateRequest[];
                nextCursor?: string;
              }>
            >(finalUrl, { headers })
          )
        )
      ),
      catchError(
        this.handleError<
          ApiResponse<{
            items: AccountUpdateRequest[];
            nextCursor?: string;
          }>
        >('getAccountUpdateRequests')
      )
    );
  }

  // Get account allowances
  getAccountAllowances(
    account: CreditAccount
  ): Observable<ApiResponse<AgentAllowance[]>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    let url = `${this.baseUrl}/${encodeURIComponent(account.id!)}/allowances`;
    if (account.owner?.id) {
      if (account.owner.type === UserType.AGENT) {
        url += `?agentId=${encodeURIComponent(account.owner.id)}`;
      } else {
        url += `?agencyId=${encodeURIComponent(account.owner.id)}`;
      }
    }

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        from(this.authService.currentUser$).pipe(
          take(1),
          map(currentUser => {
            let finalUrl = url;
            if (
              currentUser?.userType === UserType.AGENCY &&
              account.owner?.type === UserType.AGENT &&
              currentUser.id
            ) {
              if (!finalUrl.includes('agencyId=')) {
                finalUrl +=
                  (finalUrl.includes('?') ? '&' : '?') +
                  `agencyId=${encodeURIComponent(currentUser.id)}`;
              }
            }
            return { headers, finalUrl };
          }),
          switchMap(({ headers, finalUrl }) =>
            this.http.get<ApiResponse<AgentAllowance[]>>(finalUrl, { headers })
          )
        )
      ),
      catchError(
        this.handleError<ApiResponse<AgentAllowance[]>>('getAccountAllowances')
      )
    );
  }

  // Update allowance
  updateAllowance(
    account: CreditAccount,
    allowanceId: string,
    updates: Partial<AgentAllowance>
  ): Observable<ApiResponse<AgentAllowance>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }

    console.log('updateAllowance', updates);

    const payload = AgentAllowance.toJSON(updates) as {
      [key: string]: unknown;
    };

    payload['owner'] = UserBasicContainer.toJSON(account.owner!);

    console.log('updateAllowance payload', payload);

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.put<ApiResponse<AgentAllowance>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/allowances/${encodeURIComponent(allowanceId)}`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Allowance updated successfully');
        }
        return response;
      }),
      catchError(
        this.handleError<ApiResponse<AgentAllowance>>('updateAllowance')
      )
    );
  }

  // Delete allowance
  deleteAllowance(
    account: CreditAccount,
    allowanceId: string,
    skipSuccessToast: boolean = false
  ): Observable<ApiResponse<void>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.delete<ApiResponse<void>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/allowances/${encodeURIComponent(allowanceId)}?agencyId=${encodeURIComponent(account.owner?.id || '')}`,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          if (!skipSuccessToast) {
            this.toastService.success('Allowance deleted successfully');
          }
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<void>>('deleteAllowance'))
    );
  }

  // Lock funds
  lockFunds(
    account: CreditAccount,
    amount: number,
    reason: string,
    type: FundLockType = FundLockType.FLT_RESERVATION,
    expiresAt?: Date
  ): Observable<ApiResponse<FundLock>> {
    if (!account.id || !account.owner?.id || !account.owner?.type) {
      throw new Error('Account ID is required');
    }

    const payload = FundLock.toJSON({
      amount,
      reason,
      type,
      status: FundLockStatus.FLS_ACTIVE,
      expiresAt: expiresAt ? parseDateContainer(expiresAt) : undefined,
    }) as { [key: string]: unknown };

    payload['owner'] = UserBasicContainer.toJSON(account.owner);

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<FundLock>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/lock-funds`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Funds locked successfully');
          this.getAccountBalance(account).subscribe(); // Refresh balance
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<FundLock>>('lockFunds'))
    );
  }

  // Release fund lock
  releaseLock(
    account: CreditAccount,
    lockId: string
  ): Observable<ApiResponse<FundLock>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    const payload = { owner: account.owner, lockId };

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<FundLock>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/release-lock`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Fund lock released successfully');
          this.getAccountBalance(account).subscribe(); // Refresh balance
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<FundLock>>('releaseLock'))
    );
  }

  // Get active fund locks for account
  getAccountFundLocks(accountId: string): Observable<ApiResponse<Payment[]>> {
    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http
          .get<
            ApiResponse<Payment[]>
          >(`${this.baseUrl}/${encodeURIComponent(accountId)}/fund-locks`, { headers })
          .pipe(
            map(response => ({
              ...response,
              data: response.data?.map(payment => Payment.fromJSON(payment)),
            }))
          )
      ),
      catchError(
        this.handleError<ApiResponse<Payment[]>>('getAccountFundLocks')
      )
    );
  }

  // Transfer funds between accounts
  transferFunds(
    fromAccount: CreditAccount,
    toAccount: {
      id: string;
      owner?: UserBasicContainer;
    },
    amount: number,
    description?: string,
    receiver?: any,
    agencyId?: string
  ): Observable<ApiResponse<any>> {
    console.log('transferFunds', {
      fromAccount,
      toAccount,
      amount,
      description,
      receiver,
      agencyId,
    });
    const payload: { [key: string]: unknown } = {
      toAccount: {
        id: toAccount.id,
        owner: UserBasicContainer.toJSON(toAccount.owner!),
      },
      amount,
      description,
      receiver,
      agencyId,
    };

    payload['owner'] = UserBasicContainer.toJSON(fromAccount.owner!);

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<any>>(
          `${this.baseUrl}/${encodeURIComponent(fromAccount.id!)}/transfer`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Transfer completed successfully');
          this.getAccountBalance(fromAccount).subscribe(); // Refresh source balance
          this.getAccountBalance(toAccount).subscribe(); // Refresh destination balance
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<any>>('transferFunds'))
    );
  }

  // Add funds to account
  addFunds(
    account: CreditAccount,
    amount: number,
    source: string,
    description?: string,
    metadata?: any,
    agencyId?: string
  ): Observable<ApiResponse<any>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    const payload: { [key: string]: unknown } = {
      amount,
      source,
      description,
      metadata,
      agencyId,
    };

    payload['owner'] = UserBasicContainer.toJSON(account.owner!);

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<any>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/add-funds`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Funds added successfully');
          this.getAccountBalance(account).subscribe(); // Refresh balance
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<any>>('addFunds'))
    );
  }

  // Process refund
  processRefund(
    account: CreditAccount,
    originalPaymentId: string,
    refundAmount: number,
    reason?: string
  ): Observable<ApiResponse<any>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    const payload = {
      originalPaymentId,
      refundAmount,
      reason,
    };

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.post<ApiResponse<any>>(
          `${this.baseUrl}/${encodeURIComponent(account.id!)}/refund`,
          payload,
          { headers }
        )
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Refund processed successfully');
          this.getAccountBalance(account).subscribe(); // Refresh balance
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<any>>('processRefund'))
    );
  }

  // Get account transactions
  getAccountTransactions(
    account: CreditAccount,
    limit: number = 50,
    offset: number = 0
  ): Observable<ApiResponse<any>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    let url = `${this.baseUrl}/${encodeURIComponent(account.id!)}/transactions?limit=${limit}&offset=${offset}`;
    if (account.owner?.id) {
      url += `&agencyId=${encodeURIComponent(account.owner.id)}`;
    }

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers => this.http.get<ApiResponse<any>>(url, { headers })),
      catchError(this.handleError<ApiResponse<any>>('getAccountTransactions'))
    );
  }

  // Get agencies with access
  getAgenciesWithAccess(): Observable<
    ApiResponse<{ agencies: string[]; hasAccess: boolean }>
  > {
    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<ApiResponse<{ agencies: string[]; hasAccess: boolean }>>(
          `${this.baseUrl}/agencies-with-access`,
          { headers }
        )
      ),
      catchError(
        this.handleError<
          ApiResponse<{ agencies: string[]; hasAccess: boolean }>
        >('getAgenciesWithAccess')
      )
    );
  }

  /**
   * AE-5597: direct downline agencies whose default wallet the given parent
   * agency can transfer into. Optional to the caller, so a failure resolves to
   * an empty list instead of toasting.
   */
  loadDownlineAgencies(agencyId: string): Observable<DownlineAgency[]> {
    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.get<ApiResponse<{ agencies: DownlineAgency[] }>>(
          `${this.baseUrl}/downline-agencies?agencyId=${encodeURIComponent(agencyId)}`,
          { headers }
        )
      ),
      map(response => response.data?.agencies ?? []),
      catchError(error => {
        console.error('loadDownlineAgencies failed', error);
        return of([]);
      })
    );
  }

  // Update account settings
  updateAccountSettings(
    account: CreditAccount,
    settings: any
  ): Observable<ApiResponse<any>> {
    if (!account.id) {
      throw new Error('Account ID is required');
    }
    const payload = CreditAccount.toJSON({
      owner: account.owner,
      ...settings,
    });

    let path = `${this.baseUrl}/${encodeURIComponent(account.id!)}/settings`;
    if (account.owner?.id && account.owner.type === UserType.AGENCY) {
      path += `?agencyId=${encodeURIComponent(account.owner.id)}`;
    }

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers =>
        this.http.put<ApiResponse<any>>(path, payload, { headers })
      ),
      map(response => {
        if (response.success) {
          this.toastService.success('Account settings updated successfully');
          // Refresh account data
          this.getAccount(account).subscribe();
        }
        return response;
      }),
      catchError(this.handleError<ApiResponse<any>>('updateAccountSettings'))
    );
  }

  listPaymentMethods() {
    const url = `${environment.payments.endpoint}/dialer/stripe/payment-methods`;

    return from(this.getAuthHeaders()).pipe(
      switchMap(headers => this.http.get<ApiResponse<any>>(url, { headers })),
      map(response => {
        return response;
      }),
      catchError(this.handleError<ApiResponse<any>>('listPaymentMethods'))
    );
  }
}
